import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import { HealthStatus } from '../helpers/reports/areaHealthStatus';

export interface AreaHealthStatusRow {
    areaId: number;
    latestAt: string | null;
    dayStart: string | null;
    dayEnd: string | null;
    totalCount: number;
    outOfRangeCount: number;
    percentOutOfRange: number;
    status: HealthStatus;
}

interface UseAreaHealthStatusesOptions {
    enabled?: boolean;
}

const useAreaHealthStatuses = (areaIds: number[], options: UseAreaHealthStatusesOptions = {}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [statuses, setStatuses] = useState<AreaHealthStatusRow[]>([]);

    const targetIds = useMemo(() => {
        if (!areaIds || areaIds.length === 0) return [];
        const seen = new Set<number>();
        return areaIds
            .map((id) => Number(id))
            .filter((id) => !Number.isNaN(id))
            .filter((id) => {
                if (seen.has(id)) return false;
                seen.add(id);
                return true;
            });
    }, [areaIds]);

    const areaKey = useMemo(() => targetIds.join(','), [targetIds]);
    const targetIdsRef = useRef(targetIds);

    useEffect(() => {
        targetIdsRef.current = targetIds;
    }, [targetIds]);

    useEffect(() => {
        if (options.enabled === false) {
            setStatuses([]);
            setError(null);
            setLoading(false);
            return;
        }

        let isMounted = true;
        const controller = new AbortController();

        const loadStatuses = async () => {
            const resolvedIds = targetIdsRef.current;
            if (resolvedIds.length === 0) {
                if (isMounted) {
                    setStatuses([]);
                    setError(null);
                    setLoading(false);
                }
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const res = await axios.get(ENDPOINTS.AREA_HEALTH_STATUS, {
                    params: { areaIds: resolvedIds.join(',') },
                    withCredentials: true,
                    signal: controller.signal,
                });
                const payload = res.data?.data || res.data || [];
                if (isMounted) {
                    setStatuses(Array.isArray(payload) ? payload : []);
                }
            } catch (err: any) {
                if (axios.isCancel(err) || err.name === 'CanceledError') {
                    return;
                }
                if (isMounted) {
                    setError('Could not load area health status.');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        void loadStatuses();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [areaKey, options.enabled]);

    return { loading, error, statuses };
};

export default useAreaHealthStatuses;
